import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Trash2, Download, Eye, Calendar, User, Mail, Phone, MessageSquare, Filter } from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

interface Lead {
  id: string;
  form_type: string;
  form_data: any;
  page_url: string;
  referrer: string;
  utm_source: string;
  utm_medium: string;
  utm_campaign: string;
  status: string;
  created_at: string;
}

const fetchLeads = async (): Promise<Lead[]> => {
  const { data, error } = await supabase
    .from('form_submissions')
    .select('*')
    .order('created_at', { ascending: false });

  if (error) throw error;
  return data || [];
};

export const LeadsManager = () => {
  const [selectedStatus, setSelectedStatus] = useState<string>('all');
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: leads = [], isLoading, error } = useQuery({
    queryKey: ['leads'],
    queryFn: fetchLeads,
  });

  const deleteLead = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('form_submissions')
        .delete()
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leads'] });
      toast({
        title: "Lead supprimé",
        description: "Le lead a été supprimé avec succès.",
      });
    },
    onError: () => {
      toast({
        title: "Erreur",
        description: "Impossible de supprimer le lead.",
        variant: "destructive",
      });
    },
  });

  const updateLeadStatus = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: string }) => {
      const { error } = await supabase
        .from('form_submissions')
        .update({ status })
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leads'] });
      toast({
        title: "Statut mis à jour",
        description: "Le statut du lead a été mis à jour.",
      });
    },
  });

  const exportLeads = () => {
    const csvContent = [
      ['Date', 'Nom', 'Email', 'Téléphone', 'Type', 'Statut', 'Source', 'Message'].join(','),
      ...filteredLeads.map(lead => [
        format(new Date(lead.created_at), 'dd/MM/yyyy HH:mm', { locale: fr }),
        lead.form_data?.name || '',
        lead.form_data?.email || '',
        lead.form_data?.phone || '',
        lead.form_type,
        lead.status,
        lead.utm_source || lead.referrer || 'Direct',
        (lead.form_data?.message || '').replace(/,/g, ';')
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `leads_${format(new Date(), 'yyyy-MM-dd')}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const filteredLeads = leads.filter(lead => {
    if (selectedStatus === 'all') return true;
    return lead.status === selectedStatus;
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'secondary';
      case 'contacted': return 'default';
      case 'converted': return 'default';
      case 'lost': return 'destructive';
      default: return 'secondary';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'pending': return 'En attente';
      case 'contacted': return 'Contacté';
      case 'converted': return 'Converti';
      case 'lost': return 'Perdu';
      default: return status;
    }
  };

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/4"></div>
          <div className="space-y-2">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-20 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <Card>
          <CardContent className="p-6">
            <p className="text-destructive">Erreur lors du chargement des leads</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold">Gestion des Leads</h2>
          <p className="text-muted-foreground">Gérez vos leads et suivez les conversions</p>
        </div>
        
        <div className="flex gap-2">
          <Button onClick={exportLeads} variant="outline">
            <Download className="h-4 w-4 mr-2" />
            Exporter CSV
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Filter className="h-5 w-5" />
            Filtres
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex gap-2">
            <Button
              variant={selectedStatus === 'all' ? 'default' : 'outline'}
              onClick={() => setSelectedStatus('all')}
              size="sm"
            >
              Tous ({leads.length})
            </Button>
            <Button
              variant={selectedStatus === 'pending' ? 'default' : 'outline'}
              onClick={() => setSelectedStatus('pending')}
              size="sm"
            >
              En attente ({leads.filter(l => l.status === 'pending').length})
            </Button>
            <Button
              variant={selectedStatus === 'contacted' ? 'default' : 'outline'}
              onClick={() => setSelectedStatus('contacted')}
              size="sm"
            >
              Contactés ({leads.filter(l => l.status === 'contacted').length})
            </Button>
            <Button
              variant={selectedStatus === 'converted' ? 'default' : 'outline'}
              onClick={() => setSelectedStatus('converted')}
              size="sm"
            >
              Convertis ({leads.filter(l => l.status === 'converted').length})
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Leads Summary */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Leads</CardTitle>
            <User className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{leads.length}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">En Attente</CardTitle>
            <Calendar className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-orange-600">
              {leads.filter(l => l.status === 'pending').length}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Convertis</CardTitle>
            <Eye className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">
              {leads.filter(l => l.status === 'converted').length}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Taux de Conversion</CardTitle>
            <Eye className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-600">
              {leads.length > 0 
                ? ((leads.filter(l => l.status === 'converted').length / leads.length) * 100).toFixed(1)
                : '0'}%
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Leads List */}
      <Card>
        <CardHeader>
          <CardTitle>Liste des Leads</CardTitle>
          <CardDescription>
            {filteredLeads.length} lead(s) {selectedStatus !== 'all' && `avec le statut "${getStatusLabel(selectedStatus)}"`}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {filteredLeads.map((lead) => (
              <div key={lead.id} className="border rounded-lg p-4">
                <div className="flex justify-between items-start mb-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <h3 className="font-semibold">{lead.form_data?.name || 'Nom non fourni'}</h3>
                      <Badge variant={getStatusColor(lead.status)}>
                        {getStatusLabel(lead.status)}
                      </Badge>
                      <Badge variant="outline">{lead.form_type}</Badge>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-muted-foreground">
                      <div className="flex items-center gap-1">
                        <Mail className="h-3 w-3" />
                        {lead.form_data?.email || 'Email non fourni'}
                      </div>
                      <div className="flex items-center gap-1">
                        <Phone className="h-3 w-3" />
                        {lead.form_data?.phone || 'Téléphone non fourni'}
                      </div>
                      <div className="flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        {format(new Date(lead.created_at), 'dd/MM/yyyy à HH:mm', { locale: fr })}
                      </div>
                      <div className="flex items-center gap-1">
                        <Eye className="h-3 w-3" />
                        {lead.utm_source || lead.referrer || 'Direct'}
                      </div>
                    </div>

                    {lead.form_data?.eventDate && (
                      <div className="mt-2 text-sm">
                        <strong>Date événement:</strong> {lead.form_data.eventDate}
                      </div>
                    )}

                    {lead.form_data?.products && (
                      <div className="mt-2 text-sm">
                        <strong>Produits:</strong> {
                          Object.entries(lead.form_data.products)
                            .filter(([_, selected]) => selected)
                            .map(([product]) => product)
                            .join(', ')
                        }
                        {lead.form_data?.chateauType && ` (${lead.form_data.chateauType})`}
                      </div>
                    )}

                    {lead.form_data?.message && (
                      <div className="mt-2 text-sm">
                        <div className="flex items-start gap-1">
                          <MessageSquare className="h-3 w-3 mt-0.5" />
                          <span className="italic">"{lead.form_data.message}"</span>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="flex gap-2 ml-4">
                    <select
                      value={lead.status}
                      onChange={(e) => updateLeadStatus.mutate({ id: lead.id, status: e.target.value })}
                      className="text-xs border rounded px-2 py-1"
                      disabled={updateLeadStatus.isPending}
                    >
                      <option value="pending">En attente</option>
                      <option value="contacted">Contacté</option>
                      <option value="converted">Converti</option>
                      <option value="lost">Perdu</option>
                    </select>
                    
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => {
                        if (confirm('Êtes-vous sûr de vouloir supprimer ce lead ?')) {
                          deleteLead.mutate(lead.id);
                        }
                      }}
                      disabled={deleteLead.isPending}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}

            {filteredLeads.length === 0 && (
              <div className="text-center py-8 text-muted-foreground">
                {selectedStatus === 'all' 
                  ? 'Aucun lead pour le moment.'
                  : `Aucun lead avec le statut "${getStatusLabel(selectedStatus)}".`
                }
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};